import React, { useState } from 'react'
import { Route, Switch, useRouteMatch } from 'react-router'

// Utils
import { positionDefault } from '@reach/popover'
import {
  routes,
  isWorkspaceRoute,
  isProjectRoute,
  isProjectTrashRoute,
  isDraftsRoute,
} from '@sketch/modules-common'
import {
  useSearchFilters,
  ShareSearchFilter,
} from 'modules/shares/hooks/useSearchFilters'

// Hooks
import { useSearch } from 'modules/shares/hooks/useSearch'
import { useHideCollectionsSetting } from 'modules/collections/hooks'

// Components
import {
  Dropdown,
  SelectTriggerUnstyled,
  Tooltip,
  TooltipShortcut,
  useForTablet,
  Modal,
  useModalContext,
  Button,
  Box,
} from '@sketch/components'

// Graphql
import {
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

// Styles
import {
  Filter as SearchInput,
  IconWrapper,
  StyledCheckbox,
  StyledChevron,
  StyledDropdownItem,
  StyledSelectPopover,
} from './SearchBar.styles'
import {
  IconButton,
  StyledDropdownHeader,
  StyledResetIcon,
  StyledSelectInput,
  ToggleWrapper,
} from './SearchBarWithFilters.styles'

export const getPlaceholderByPath = (path: string) => {
  switch (path) {
    case routes.WORKSPACE_TRASH.template():
      return 'Search all documents in Trash'
    case routes.WORKSPACE_LIBRARIES.template():
    case routes.DESIGN_SYSTEM.template():
      return 'Search Libraries'
    case routes.WORKSPACE_SHARED_WITH_ME.template():
      return 'Search Shared with Me'
    case routes.WORKSPACE_TEMPLATES.template():
      return 'Search Templates'
    case routes.WORKSPACE_DRAFTS.template():
      return 'Search My Drafts'
    default:
      return 'Search'
  }
}

const FiltersMobileModal: React.FC = ({ children }) => {
  const { hideModal } = useModalContext()

  return (
    <Modal onCancel={hideModal}>
      <Modal.Body>
        {children}
        <Box pt={6}>
          <Button variant="secondary" fill onClick={hideModal}>
            Done
          </Button>
        </Box>
      </Modal.Body>
    </Modal>
  )
}

interface FiltersProps {
  userCanToggleProjects: boolean
}

const Filters: React.FC<FiltersProps> = ({
  userCanToggleProjects,
}: FiltersProps) => {
  const isTabletAndBigger = useForTablet()
  const { showModal } = useModalContext()
  const { path } = useRouteMatch()

  const { filters, setFilters } = useSearchFilters()
  const { data, updateQuery } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects = true } = data || {}
  const { hideCollections, setHideCollections } = useHideCollectionsSetting()

  const canResetShowDocumentsInProjects = isWorkspaceRoute(path)
  const canResetHideCollections =
    isProjectRoute(path) || isProjectTrashRoute(path) || isDraftsRoute(path)

  const toggleFilter = (filter: ShareSearchFilter) => {
    if (filters.includes(filter)) {
      setFilters(filters.filter(x => x !== filter))
    } else {
      setFilters([filter, ...filters])
    }
  }

  const clearFilters = () => {
    setFilters([])
  }

  const reset = () => {
    clearFilters()

    if (canResetShowDocumentsInProjects) {
      updateQuery(previous => ({
        ...previous,
        showDocumentsInProjects: true,
      }))
    }

    if (canResetHideCollections) {
      setHideCollections(false)
    }
  }

  const resetActive =
    filters.length > 0 ||
    (canResetShowDocumentsInProjects && showDocumentsInProjects === false) ||
    (canResetHideCollections && hideCollections === true)

  const filterContent = (
    <>
      <StyledDropdownHeader>
        <span>Documents</span>
        <Tooltip content="Reset" placement="top">
          <IconButton onClick={reset}>
            <StyledResetIcon $active={resetActive} />
          </IconButton>
        </Tooltip>
      </StyledDropdownHeader>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={filters.length === 0}
          label="All documents"
          onChange={() => clearFilters()}
        />
      </StyledDropdownItem>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={filters.includes('HAS_PROTOTYPES')}
          label="With prototypes"
          onChange={() => toggleFilter('HAS_PROTOTYPES')}
        />
      </StyledDropdownItem>

      <StyledDropdownItem>
        <StyledCheckbox
          checked={filters.includes('I_CAN_EDIT')}
          label="You can edit"
          onChange={() => toggleFilter('I_CAN_EDIT')}
        />
      </StyledDropdownItem>

      <Switch>
        <Route path={routes.WORKSPACE_SHARES.template()}>
          {userCanToggleProjects && <ToggleProjectsCheckbox />}
        </Route>

        <Route
          path={[
            routes.WORKSPACE_PROJECT.template(),
            routes.WORKSPACE_TRASH_PROJECT.template(),
            routes.WORKSPACE_DRAFTS.template(),
          ]}
          exact
        >
          <ToggleCollectionsCheckbox />
        </Route>
      </Switch>
    </>
  )

  // show modal if mobile
  if (!isTabletAndBigger) {
    return (
      <>
        <ToggleWrapper
          onClick={() =>
            showModal(FiltersMobileModal, {
              children: filterContent,
            })
          }
        >
          Filter
          <IconWrapper>
            <StyledChevron />
          </IconWrapper>
        </ToggleWrapper>
      </>
    )
  }

  return (
    <StyledSelectInput
      data-testid="filter-dropdown"
      value={'Filter'}
      onKeyUp={() => null}
    >
      <SelectTriggerUnstyled>
        <ToggleWrapper>
          Filter
          <IconWrapper>
            <StyledChevron />
          </IconWrapper>
        </ToggleWrapper>
      </SelectTriggerUnstyled>
      <StyledSelectPopover position={positionDefault}>
        {filterContent}
      </StyledSelectPopover>
    </StyledSelectInput>
  )
}

const ToggleProjectsCheckbox: React.FC<{}> = () => {
  const { data, updateQuery } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects } = data || {}

  return (
    <>
      <Dropdown.Divider />

      <StyledDropdownItem>
        <StyledCheckbox
          checked={showDocumentsInProjects}
          label="Include documents in Projects"
          onChange={() =>
            updateQuery(previous => ({
              ...previous,
              showDocumentsInProjects: !showDocumentsInProjects,
            }))
          }
        />
      </StyledDropdownItem>
    </>
  )
}

const ToggleCollectionsCheckbox: React.FC<{}> = () => {
  const { hideCollections, setHideCollections } = useHideCollectionsSetting()

  const toggleHideCollections = () => {
    setHideCollections(!hideCollections)
  }

  return (
    <>
      <Dropdown.Divider />

      <StyledDropdownItem>
        <StyledCheckbox
          checked={hideCollections}
          label="Hide Collections"
          help="Documents in collections will appear in the project."
          onChange={toggleHideCollections}
        />
      </StyledDropdownItem>
    </>
  )
}

interface SearchBarWithFiltersProps {
  userRole: WorkspaceMinimalFragment['userRole']
}

const SearchBarWithFilters: React.FC<SearchBarWithFiltersProps> = ({
  userRole,
}: SearchBarWithFiltersProps) => {
  const { search, setSearch } = useSearch()
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const userCanToggleProjects = userRole !== 'GUEST'
  const { path } = useRouteMatch()
  const inputPlaceholder = getPlaceholderByPath(path)

  return (
    <Tooltip
      content={
        <>
          Search
          <TooltipShortcut>Press /</TooltipShortcut>
        </>
      }
      disableWhenTouchDevice
      placement="bottom-start"
      visible={tooltipVisible}
    >
      <div
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <SearchInput
          onChange={setSearch}
          value={search}
          name="search"
          placeholder={inputPlaceholder}
        >
          <Route
            path={[
              routes.WORKSPACE_SHARES.template(),
              routes.WORKSPACE_DRAFTS.template(),
              routes.WORKSPACE_LIBRARIES.template(),
              routes.WORKSPACE_SHARED_WITH_ME.template(),
              routes.WORKSPACE_TEMPLATES.template(),
              routes.WORKSPACE_PROJECT.template(),
              routes.WORKSPACE_TRASH_PROJECT.template(),
              routes.WORKSPACE_COLLECTION.template(),
              routes.WORKSPACE_TRASH_COLLECTION.template(),
            ]}
          >
            <div onMouseEnter={() => setTooltipVisible(false)}>
              <Filters userCanToggleProjects={userCanToggleProjects} />
            </div>
          </Route>
        </SearchInput>
      </div>
    </Tooltip>
  )
}

export default SearchBarWithFilters
